/**
 * サーバー時刻オフセット補正（S1対応）
 *
 * 端末の時計はサーバー時刻と数秒単位でずれていることがあり、
 * 締切判定（scheduled_close_time との比較）を端末時刻のまま行うと
 * タイマー表示・未回答送信・リトライ打ち切りがサーバーの受付窓とずれる。
 *
 * REST API（GET /questions/current）のレスポンスに含まれる server_time と
 * リクエストの往復時間（RTT）からオフセットを計測し、
 * serverNow() でサーバー時刻基準の現在時刻を提供する。
 */

interface OffsetSample {
  /** サーバー時刻 − 端末時刻（ms）。正なら端末の時計が遅れている */
  offset: number;
  /** 計測時の往復時間（ms）。小さいほどオフセットの精度が高い */
  rtt: number;
  /** 計測した端末時刻（ms） */
  measuredAt: number;
}

/** 採用中のサンプル。RTT が最小のものを保持する */
let bestSample: OffsetSample | null = null;

/** 古いサンプルの破棄期限（講義中に端末の時計が同期し直される可能性を考慮） */
const SAMPLE_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * サーバー時刻オフセットを計測・更新する。
 *
 * offset = server_time − (リクエスト開始時刻 + RTT/2)
 * （往復遅延が対称と仮定し、レスポンス生成時点の端末時刻を推定して差を取る）
 *
 * RTT が現在の採用サンプルより小さい場合、または採用サンプルが期限切れの場合に更新する。
 *
 * @param serverTime レスポンスに含まれるサーバー時刻（epoch ms）
 * @param requestStartMs リクエスト送信直前の端末時刻（Date.now()）
 * @param responseEndMs レスポンス受信直後の端末時刻（Date.now()）
 */
export function updateServerTimeOffset(
  serverTime: number,
  requestStartMs: number,
  responseEndMs: number,
): void {
  if (!Number.isFinite(serverTime) || serverTime <= 0) return;

  const rtt = Math.max(0, responseEndMs - requestStartMs);
  const offset = serverTime - (requestStartMs + rtt / 2);

  const expired =
    bestSample !== null && Date.now() - bestSample.measuredAt > SAMPLE_MAX_AGE_MS;

  if (bestSample === null || expired || rtt <= bestSample.rtt) {
    bestSample = { offset, rtt, measuredAt: Date.now() };
  }
}

/**
 * サーバー時刻基準の現在時刻（epoch ms）を返す。
 * 未計測の場合はオフセット0（= 端末時刻）で動作する（後方互換）。
 */
export function serverNow(): number {
  return Date.now() + (bestSample?.offset ?? 0);
}
